import api from './api';
import {
  SubscriptionAudience,
  SubscriptionPlan,
  Subscription,
  SubscriptionDetails,
  CreateSubscriptionDto,
  UpdateSubscriptionDto,
  CancelSubscriptionDto,
  UpgradeDowngradeDto,
  PaymentHistory,
  SubscriptionProrationPreview,
} from '../types/subscription';

const toSubscriptionDetails = (raw: any): SubscriptionDetails => {
  if (raw?.currentSubscription) {
    return raw as SubscriptionDetails;
  }

  const currentSubscription = raw as Subscription;
  const renewalDate = currentSubscription?.renewalDate || currentSubscription?.endDate;
  let daysUntilExpiry: number | undefined;
  if (renewalDate) {
    const diffMs = new Date(renewalDate).getTime() - Date.now();
    daysUntilExpiry = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  }

  return {
    currentSubscription,
    availablePlans: [],
    usageStats: {
      studentsUsed: 0,
      classesUsed: 0,
      assignmentsUsed: 0,
      examsCreated: 0,
      examAttempts: 0,
    },
    nextRenewalDate: renewalDate,
    daysUntilExpiry,
  };
};

export const subscriptionApi = {
  // Subscription Plans
  getAllPlans: async (audience?: SubscriptionAudience): Promise<SubscriptionPlan[]> => {
    const response = await api.get('/subscriptions/plans', {
      params: audience ? { audience } : undefined,
    });
    return response.data;
  },

  getPlanById: async (planId: string): Promise<SubscriptionPlan> => {
    const response = await api.get(`/subscriptions/plans/${planId}`);
    return response.data;
  },

  createPlan: async (data: any): Promise<SubscriptionPlan> => {
    const response = await api.post('/subscriptions/plans', data);
    return response.data;
  },

  updatePlan: async (planId: string, data: any): Promise<SubscriptionPlan> => {
    const response = await api.put(`/subscriptions/plans/${planId}`, data);
    return response.data;
  },

  deletePlan: async (planId: string): Promise<void> => {
    await api.delete(`/subscriptions/plans/${planId}`);
  },

  // User Subscription
  getCurrentSubscription: async (): Promise<SubscriptionDetails> => {
    const response = await api.get('/subscriptions/me');
    return toSubscriptionDetails(response.data);
  },

  previewPlanSwitch: async (planId: string): Promise<SubscriptionProrationPreview> => {
    const response = await api.get(`/subscriptions/me/switch-preview/${planId}`);
    return response.data;
  },

  getUserSubscription: async (userId: string): Promise<Subscription> => {
    const response = await api.get(`/subscriptions/${userId}`);
    return response.data;
  },

  createSubscription: async (data: CreateSubscriptionDto): Promise<Subscription> => {
    const response = await api.post('/subscriptions', data);
    return response.data;
  },

  upgradeSubscription: async (data: UpgradeDowngradeDto): Promise<Subscription> => {
    const response = await api.put(`/subscriptions/me/upgrade/${data.newPlanId}`);
    return response.data;
  },

  downgradeSubscription: async (data: UpgradeDowngradeDto): Promise<Subscription> => {
    const response = await api.post('/subscriptions/downgrade', data);
    return response.data;
  },

  updateSubscription: async (_subscriptionId: string, data: UpdateSubscriptionDto): Promise<Subscription> => {
    const response = await api.put('/subscriptions/me', data);
    return response.data;
  },

  cancelSubscription: async (data: CancelSubscriptionDto): Promise<Subscription> => {
    const response = await api.post('/subscriptions/me/cancel', data);
    return response.data;
  },

  renewSubscription: async (): Promise<Subscription> => {
    const response = await api.post('/subscriptions/renew');
    return response.data;
  },

  // Payment History
  getPaymentHistory: async (): Promise<PaymentHistory[]> => {
    const response = await api.get('/subscriptions/payments/history');
    return response.data;
  },

  getPaymentDetails: async (paymentId: string): Promise<PaymentHistory> => {
    const response = await api.get(`/subscriptions/payments/${paymentId}`);
    return response.data;
  },

  downloadInvoice: async (paymentId: string): Promise<Blob> => {
    const response = await api.get(`/subscriptions/payments/${paymentId}/invoice`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Stripe Integration
  createPaymentIntent: async (planId: string, billingCycle: string): Promise<any> => {
    const response = await api.post('/subscriptions/payment-intent', {
      planId,
      billingCycle,
    });
    return response.data;
  },

  confirmPayment: async (paymentIntentId: string): Promise<Subscription> => {
    const response = await api.post('/subscriptions/confirm-payment', {
      paymentIntentId,
    });
    return response.data;
  },
};

export default subscriptionApi;
